import React, { useState, useEffect } from "react";
import Banner from "./Banner";
import TerminalOutput from "./TerminalOutput";
import InputArea from "./InputArea";
import ErrorMessage from "./ErrorMessage";
import WelcomeMessage from "./WelcomeMessage";

// Just a little helper function so I don't have to continually update my age
const getAge = (birthDate: Date) => {
  var today = new Date();
  var age = today.getFullYear() - birthDate.getFullYear();
  var m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const downloadFile = (uri: string, downloadName: string) => {
  const link = document.createElement("a");
  link.download = downloadName;
  link.href = uri;
  link.click();
  link.remove();
};

type TerminalProps = {
  terminalPrompt?: string;
  banner?: string;
  welcomeMessage?: string;
};
const Terminal = (props: TerminalProps) => {
  const { terminalPrompt = ">", banner, welcomeMessage } = props;
  const [output, setOutput] = useState<(string | JSX.Element)[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(3);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const scrollLastCommandTop = () => {
    scrollRef.current?.scrollIntoView();
  };

  useEffect(scrollLastCommandTop, [output]);

  const echoCommands = [
    "help",
    "about",
    "contact",
    "portfolio",
    "principals",
    "pricing",
   ] as const;
  type EchoCommand = typeof echoCommands[number];
  const utilityCommands = ["clear", "all", "download_cv"] as const;
  type UtilityCommand = typeof utilityCommands[number];
  const allCommands = [...echoCommands, ...utilityCommands] as const;
  type Command = typeof allCommands[number];

  function isEchoCommand(arg: string): arg is EchoCommand {
    return (echoCommands as ReadonlyArray<string>).includes(arg);
  }

  function isUtilityCommand(arg: string): arg is UtilityCommand {
    return (utilityCommands as ReadonlyArray<string>).includes(arg);
  }

  function isValidCommand(arg: string): arg is Command {
    return isEchoCommand(arg) || isUtilityCommand(arg);
  }

  const glow = (text: string) => {
    return <span className="terminal-glow">{text}</span>;
  };

  const commands: { [key in EchoCommand]: JSX.Element } = {
    help: (
      <div>
        <p>
          Type any of the commands below
          to get some more info. You can even type a few letters and press [tab]
          or '.' to autocomplete.
        </p>
        <dl>
          <dt>about</dt>
          <dd>Learn a little about us</dd>
          <dt>principals</dt>
          <dd>Check out what our business values</dd>
          <dt>portfolio</dt>
          <dd>Take a look at some of our work</dd>
         {/*  <dt>download_cv</dt>
          <dd>Check out my CV [pdf - 168KB]</dd> */}
          <dt>contact</dt>
          <dd>Contact Us</dd>
          <dt>pricing</dt>
          <dd>Our Pricing information</dd>
          <dt>all</dt>
          <dd>Tell me everything</dd>
          <dt>clear</dt>
          <dd>Clears the terminal of all output</dd>
        </dl>
      </div>
    ),
    about: (
      <div>
        <p>
          Salutations! Thanks for taking the Time to check us out!  
          We are a boutique Web Design Studio, with experience in small personal projects and large enterprise information systems
        </p>
        <p>
         Where to begin? As you probably know, our name is {glow("Jaded Saint Designs")}
          <br />
          We are a small Development studio specializing in modern, mobile first web designs and systems.  We are lead by our
          {" "}{glow("Owner and Lead Developer, Thomas St. Germain")} Thomas is a{" "}
          {getAge(new Date(1990, 6, 7))}{" "} year old {glow("Computer Scientist")}{" "}
          born and bred in the Sunny Arizona Desert.
        </p>
       {/*  <p>
          I graduated with distinction from the University of Cape Town with a
          Bachelor of Business Science degree in Computer Science. It comprised
          of four years of computer science courses, as well as many business
          courses (for example, I completed three years of economics, stats, and
          finance).
        </p>
        <p>
          I also have an MSc degree in Computer Science from the University of
          Oxford, where I was awarded a full academic scholarship. Studying
          abroad was an amazing experience - highlights include early morning
          rowing, croquet, formal dinners, and just exploring Oxford with
          amazing people and friends.
        </p>
        <p>
          Some of my interests include{" "}
          {glow("machine learning, computer security, and cryptography")}. I'm
          also pretty into fly fishing!
        </p>
        <p>
          My previous formal work experience includes working on asset
          management software at{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.fundamental.net"
          >
            Fundamental Software
          </a>
          , as well as working for a great content creation app called{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://madewithover.com"
          >
            Over
          </a>
          .
        </p>
        <p>
          Nowadays I'm developing a method to download food... I wish! I am
          currently working at{" "}
          <a target="_blank" rel="noopener noreferrer" href="https://luno.com">
            Luno
          </a>
          , where we help people to buy, store, and learn about cryptocurrency.
        </p>
        <p>
          Please feel free to get in touch with me to discuss any cool
          opportunities. My contact details can be found by typing 'contact',
          and if you would like to check out my CV, simply type 'download_CV'.
        </p>*/}
      </div> 
    ),
    
    contact: (
      <>
        <dl>
          <dt>Email</dt>
          <dd>
            <a href="mailto:portlyostrich@outlook.com">portlyostrich@outlook.com</a>
          </dd>
          <dt>Telephone</dt>
          <dd><a href="tel:623-512-9434">623-512-9434</a></dd>
          
        </dl>
      </>
    ),
    
    portfolio: (
      <>
        <ul>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://github.com/saintmarbles"
            >
              GitHub
            </a>{" "}
            - Unfortunately, I could only make a small subset of my projects
            public.
          </li>
         
        </ul>
      </>
    ),
    principals: (
      <>
        <dl>
          <dt>Mobile First Design</dt>
          <dd>
            All of our sites are designed to work seamlessly on mobile devices.  This means there are no barriers to your customers interacting with your business
           <br />
            ##{" "}
            <span style={{ color: "#00DE12", textShadow: "0 0 5px #00DE12" }}>
              #############
            </span>{" "}
            ##
          </dd>
          <dt>No Design Limits</dt>
          <dd>
            We handcraft each site according to our customers needs, we are not limited to prebuilt designs
            <br />
            ##{" "}
            <span style={{ color: "#00DE12", textShadow: "0 0 5px #00DE12" }}>
              #############
            </span>{" "}
            ##
          </dd>
          <dt>Unbeatable Customer Service</dt>
          <dd>
            We are here to help you make your business a success.  We know every business is different, we provide first class treatment for our customers
            regardless of their size or operating hours.  We are a small US based company, no outsourcing here,  what that means is we are able to establish a report with our customers
            and cater our services to your needs.  Yo us you are not just a number on a balance sheet.   
            <br />
            ##{" "}
            <span style={{ color: "#00DE12", textShadow: "0 0 5px #00DE12" }}>
              #############
            </span>{" "}
            ##
          </dd>
          <dt>Complete Custom Treatment</dt>
          <dd>
            We dont user Sitebuilders or prebuilt templates.  These can hamper performance, accessability and restrict your sites appearance.  We handcraft each site according to our customers specifications
            <br />
            ##{" "}
            <span style={{ color: "#00DE12", textShadow: "0 0 5px #00DE12" }}>
              #############
            </span>{" "}
            ##
          </dd>
         
        </dl>
      </>
    ),
    pricing: (
      <div>
        <p>
          We know that building a website can be a large and overwhelming endeavor.  Many small business dont have the funds to pay thousands
          of dollars upfront and every month for a web presence, and yet a business without a digital component these days just doesnt feel completed 
        </p>
        <p>
        {glow("Jaded Saint")} is here to solve that problem with flexible pricing plans and even no cost upfront options.
        All of our pricing included hosting and any edits you may require.
        </p>
        <p>  
        Our focus is not to build you an application, its to help your busniess grow by assisting with whatever digital needs you may have.  
        The happiness of our customers and establishing a happy user base are our primary goals.  
        </p>
        
      
      </div> 
    ),

  };

  const processCommand = (input: string) => {
    // Store a record of this command with a ref to allow us to scroll it into view.
    // Note: We use a ref callback here because setting the ref directly, then clearing output seems to set the ref to null.
    const commandRecord = (
      <div
        ref={(el) => (scrollRef.current = el)}
        className="terminal-command-record"
      >
        <span className="terminal-prompt">{terminalPrompt}</span>{" "}
        <span>{input}</span>
      </div>
    );

    // Add command to to history if the command is not empty
    if (input.trim()) {
      setHistory([...history, input]);
      setHistoryIndex(history.length + 1);
    }

    // Now process command, ignoring case
    const inputCommand = input.toLowerCase();
    if (!isValidCommand(inputCommand)) {
      setOutput([
        ...output,
        commandRecord,
        <div className="terminal-command-output">
          <ErrorMessage command={inputCommand} />
        </div>,
      ]);
    } else if (isEchoCommand(inputCommand)) {
      setOutput([
        ...output,
        commandRecord,
        <div className="terminal-command-output">{commands[inputCommand]}</div>,
      ]);
    } else if (isUtilityCommand(inputCommand)) {
      switch (inputCommand) {
        case "clear": {
          setOutput([]);
          break;
        }
        case "all": {
          // Output all commands in a custom order.
          const allCommandsOutput = [
            "help",
            "about",
            "contact",
            "portfolio",
            "principals",
            "pricing",
          ].map((command) => (
            <>
              <div>
                <span className="terminal-prompt">--</span>{" "}
                <span>{command}</span>
              </div>
              <div className="terminal-command-output">
                {commands[command as EchoCommand]}
              </div>
            </>
          ));

          setOutput([commandRecord, ...allCommandsOutput]);
          break;
        }
        case "download_cv": {
          setOutput([...output, commandRecord]);
          downloadFile("CV.pdf", "Craig Feldman - Curriculum Vitae.pdf");
          break;
        }
      }
    }
  };

  const getHistory = (direction: "up" | "down") => {
    let updatedIndex;
    if (direction === "up") {
      updatedIndex = historyIndex === 0 ? 0 : historyIndex - 1;
    } else {
      updatedIndex =
        historyIndex === history.length ? history.length : historyIndex + 1;
    }
    setHistoryIndex(updatedIndex);
    return updatedIndex === history.length ? "" : history[updatedIndex];
  };

  const getAutocomplete = (input: string) => {
    const matchingCommands = allCommands.filter((c) => c.startsWith(input));
    if (matchingCommands.length === 1) {
      return matchingCommands[0];
    } else {
      const commandRecord = (
        <div
          ref={(el) => (scrollRef.current = el)}
          className="terminal-command-record"
        >
          <span className="terminal-prompt">{terminalPrompt}</span>{" "}
          <span>{input}</span>
        </div>
      );
      setOutput([...output, commandRecord, matchingCommands.join("    ")]);
      return input;
    }
  };

  const focusOnInput = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Tab") {
      // Prevent tab from moving focus
      event.preventDefault();
    }
    inputRef.current?.focus();
  };

  return (
    <div className="terminal-container" tabIndex={-1} onKeyDown={focusOnInput}>
      <div className="terminal-content">
        {banner && <Banner banner={banner} />}
        {welcomeMessage && (
          <WelcomeMessage message={welcomeMessage} inputRef={inputRef} />
        )}
        <TerminalOutput outputs={output} />
        <InputArea
          setOutput={setOutput}
          processCommand={processCommand}
          getHistory={getHistory}
          getAutocomplete={getAutocomplete}
          inputRef={inputRef}
          terminalPrompt={terminalPrompt}
        />
      </div>
    </div>
  );
};

export default Terminal;
